/**
 * Kanban API Client
 *
 * Manage Kanban views, columns, and board configurations.
 */

import apiClient from './client';

// Types
export interface CardSettings {
  showDescription?: boolean;
  showDueDate?: boolean;
  showAssignee?: boolean;
  showTags?: boolean;
  showCategory?: boolean;
  showSubtasks?: boolean;
  showProgress?: boolean;
  showPriority?: boolean;
  cardSize?: 'compact' | 'normal' | 'detailed';
  descriptionLines?: number;
}

export interface BoardSettings {
  defaultSort?: string;
  showColumnCounts?: boolean;
  showWipLimits?: boolean;
  columnGap?: number;
  backgroundColor?: string;
}

export interface FilterConfig {
  field?: string;
  operator?: 'eq' | 'neq' | 'contains' | 'in' | 'notin' | 'isNull' | 'isNotNull';
  value?: any;
  and?: FilterConfig[];
  or?: FilterConfig[];
}

export interface DropAction {
  setStatus?: string;
  setCategory?: string;
  addTag?: string;
}

export interface KanbanColumn {
  id: string;
  name: string;
  color: string;
  columnType: 'stage' | 'filter';
  filterConfig?: FilterConfig | null;
  dropAction: DropAction;
  width: number;
  minWidth: number;
  maxWidth: number;
  isCollapsed: boolean;
  sortOrder: number;
  wipLimit?: number | null;
}

export interface KanbanView {
  id: string;
  name: string;
  description?: string;
  entityType: 'instances' | 'templates';
  cardSettings: CardSettings;
  boardSettings: BoardSettings;
  isDefault: boolean;
  sortOrder: number;
  columnCount?: number;
  columns?: KanbanColumn[];
  createdBy?: string;
  createdByName?: string;
  createdAt: string;
  updatedAt: string;
}

export interface CreateViewInput {
  name: string;
  description?: string;
  entityType?: 'instances' | 'templates';
  cardSettings?: CardSettings;
  boardSettings?: BoardSettings;
  isDefault?: boolean;
}

export interface UpdateViewInput {
  name?: string;
  description?: string;
  cardSettings?: CardSettings;
  boardSettings?: BoardSettings;
  isDefault?: boolean;
}

export interface CreateColumnInput {
  name: string;
  color?: string;
  columnType?: 'stage' | 'filter';
  filterConfig?: FilterConfig;
  dropAction?: DropAction;
  width?: number;
  minWidth?: number;
  maxWidth?: number;
  wipLimit?: number;
  sortOrder?: number;
}

export interface UpdateColumnInput {
  name?: string;
  color?: string;
  filterConfig?: FilterConfig | null;
  dropAction?: DropAction;
  width?: number;
  sortOrder?: number;
  wipLimit?: number | null;
}

// Default card settings
export const DEFAULT_CARD_SETTINGS: CardSettings = {
  showDescription: true,
  showDueDate: true,
  showAssignee: true,
  showTags: true,
  showCategory: false,
  showSubtasks: true,
  showProgress: false,
  showPriority: false,
  cardSize: 'normal',
  descriptionLines: 2,
};

// Default board settings
export const DEFAULT_BOARD_SETTINGS: BoardSettings = {
  showColumnCounts: true,
  showWipLimits: true,
  columnGap: 16,
  backgroundColor: '#ffffff',
};

// Predefined column colors
export const COLUMN_COLORS = [
  { name: 'Gray', value: '#6b7280' },
  { name: 'Red', value: '#ef4444' },
  { name: 'Orange', value: '#f97316' },
  { name: 'Amber', value: '#f59e0b' },
  { name: 'Yellow', value: '#eab308' },
  { name: 'Lime', value: '#84cc16' },
  { name: 'Green', value: '#22c55e' },
  { name: 'Emerald', value: '#10b981' },
  { name: 'Teal', value: '#14b8a6' },
  { name: 'Cyan', value: '#06b6d4' },
  { name: 'Sky', value: '#0ea5e9' },
  { name: 'Blue', value: '#3b82f6' },
  { name: 'Indigo', value: '#6366f1' },
  { name: 'Violet', value: '#8b5cf6' },
  { name: 'Purple', value: '#a855f7' },
  { name: 'Fuchsia', value: '#d946ef' },
  { name: 'Pink', value: '#ec4899' },
  { name: 'Rose', value: '#f43f5e' },
];

// Available filter fields
export const FILTER_FIELDS = [
  { field: 'status', label: 'Status', type: 'select', options: ['pending', 'in_progress', 'completed', 'postponed', 'cancelled'] },
  { field: 'categoryId', label: 'Category', type: 'category' },
  { field: 'assignedUserId', label: 'Assignee', type: 'user' },
  { field: 'tags', label: 'Tags', type: 'tags' },
  { field: 'dueDate', label: 'Due Date', type: 'date' },
  { field: 'priority', label: 'Priority', type: 'select', options: ['low', 'medium', 'high', 'urgent'] },
];

/**
 * Kanban API methods
 */
export const kanbanApi = {
  // Views
  async listViews(): Promise<KanbanView[]> {
    const response = await apiClient.get('/v1/kanban/views');
    return response.data;
  },

  async getView(id: string): Promise<KanbanView> {
    const response = await apiClient.get(`/v1/kanban/views/${id}`);
    return response.data;
  },

  async createView(data: CreateViewInput): Promise<{ id: string }> {
    const response = await apiClient.post('/v1/kanban/views', data);
    return response.data;
  },

  async updateView(id: string, data: UpdateViewInput): Promise<void> {
    await apiClient.patch(`/v1/kanban/views/${id}`, data);
  },

  async deleteView(id: string): Promise<void> {
    await apiClient.delete(`/v1/kanban/views/${id}`);
  },

  // Columns
  async createColumn(viewId: string, data: CreateColumnInput): Promise<{ id: string }> {
    const response = await apiClient.post(`/v1/kanban/views/${viewId}/columns`, data);
    return response.data;
  },

  async updateColumn(columnId: string, data: UpdateColumnInput): Promise<void> {
    await apiClient.patch(`/v1/kanban/columns/${columnId}`, data);
  },

  async deleteColumn(columnId: string): Promise<void> {
    await apiClient.delete(`/v1/kanban/columns/${columnId}`);
  },

  async reorderColumns(viewId: string, columnIds: string[]): Promise<void> {
    await apiClient.post(`/v1/kanban/views/${viewId}/columns/reorder`, { columnIds });
  },

  async initializeDefaultColumns(viewId: string): Promise<void> {
    await apiClient.post(`/v1/kanban/views/${viewId}/initialize-default`);
  },

  // Card movement
  async moveCard(data: { instance_id: string; from_column_id?: string; to_column_id: string; sort_order?: number }): Promise<void> {
    await apiClient.post('/v1/kanban/cards/move', data);
  },
};
